import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/components/FileUpload";
import { avatarSrc } from "@/lib/defaults";
import { offeringWording } from "@/lib/offeringLabel";
import { useApp } from "@/context/AppContext";
import { CURRENCIES } from "@/lib/currencies";
import { Heart, X, Loader2, Gift } from "lucide-react";

const sym = (cur) => (CURRENCIES.find((c) => c.code === cur) || {}).symbol || cur;
const fmtAmt = (a, cur) => `${Math.round((Number(a) || 0) * 100) / 100} ${sym(cur)}`;

// Host-only recap of offerings/donations received during a specific live.
export const LiveOfferingRecap = ({ liveId, role, open, onClose }) => {
  const { t } = useApp();
  const offer = offeringWording(role, t);
  const [data, setData] = useState({ count: 0, total: 0, totals_by_currency: {}, items: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!liveId) return;
    try {
      const res = await api.get(`/live/${liveId}/donations`);
      setData(res.data);
    } catch { /* noop */ }
    setLoading(false);
  }, [liveId]);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, [open, load]);

  if (!open) return null;

  const curEntries = Object.entries(data.totals_by_currency || {});
  return (
    <div
      className="fixed inset-0 z-[90] grid place-items-center p-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="offering-recap-modal"
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden flex flex-col max-h-[80vh]"
        style={{ background: "#131A18", border: "1px solid #283832", fontFamily: "Outfit, sans-serif" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 flex items-center justify-between border-b shrink-0" style={{ borderColor: "#283832" }}>
          <div className="flex items-center gap-2 font-bold text-white">
            <Gift size={18} style={{ color: "#D97757" }} /> {offer.Plural} {offer.received}
          </div>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-full" style={{ background: "#1E2925", color: "#fff" }} data-testid="offering-recap-close"><X size={16} /></button>
        </div>

        <div className="px-4 py-3 border-b shrink-0" style={{ borderColor: "#283832" }}>
          <div className="text-xs" style={{ color: "#A0B0AA" }}>{t("live.recap_total", "Total reçu pendant ce direct")}</div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-0.5" data-testid="offering-recap-total">
            {curEntries.length === 0 ? (
              <span className="text-2xl font-extrabold" style={{ color: "#D97757" }}>0 {sym("EUR")}</span>
            ) : curEntries.map(([cur, amt]) => (
              <span key={cur} className="text-2xl font-extrabold" style={{ color: "#D97757" }}>{fmtAmt(amt, cur)}</span>
            ))}
            <span className="text-sm" style={{ color: "#7BA697" }}>· {data.count} {data.count > 1 ? offer.plural : offer.singular}</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="offering-recap-list">
          {loading && (data.items || []).length === 0 && (
            <div className="grid place-items-center py-8"><Loader2 size={22} className="animate-spin" style={{ color: "#D97757" }} /></div>
          )}
          {!loading && (data.items || []).length === 0 && (
            <p className="text-sm text-center py-8 leading-relaxed" style={{ color: "#A0B0AA" }} data-testid="offering-recap-empty">
              {t("live.recap_empty", "Rien reçu pour l'instant. Les {y} de tes spectateurs apparaîtront ici 🙏").replace("{y}", offer.plural)}
            </p>
          )}
          {(data.items || []).slice().reverse().map((d) => (
            <div key={d.id} className="rounded-xl p-3 flex items-start gap-2.5" style={{ background: "#1A221F", border: "1px solid #283832" }} data-testid="offering-recap-item">
              <img
                src={avatarSrc({ avatar_url: d.sender_avatar ? resolveMediaUrl(d.sender_avatar) : null, gender: d.sender_gender })}
                alt=""
                className="h-9 w-9 rounded-full object-cover shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm text-white truncate">{d.sender_name}</span>
                  <span className="font-bold text-sm shrink-0" style={{ color: "#D97757" }}>{fmtAmt(d.amount, d.currency)}</span>
                </div>
                {d.message ? (
                  <p className="text-xs mt-1 flex items-start gap-1.5" style={{ color: "#cfd8d4" }}>
                    <Heart size={11} className="shrink-0 mt-0.5" style={{ color: "#D97757" }} /> {d.message}
                  </p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
